/**
 * Mobile navigation
 *
 * This script adds features to show and hide the mobile navigation menu.
 *
 * The mobileBreakpoint variable must be set to the same value as the
 * CSS media query breakpoint in _main-navigation.scss and _mobile-navigation.scss,
 * as well as the desktopNavigation.js file.
 *
 * @package
 */

function closeMobileMenu() {
	const openButton = document.querySelector('.main-navigation-open');
	const closeButton = document.querySelector('.main-navigation-close');

	openButton.setAttribute('aria-expanded', 'false');
	closeButton.setAttribute('aria-expanded', 'false');
	document.body.classList.remove('main-navigation-menu-open');
	document.body.style.overflow = 'auto';

	removeMobileStyles();
}

function openMobileMenu() {
	const openButton = document.querySelector('.main-navigation-open');
	const closeButton = document.querySelector('.main-navigation-close');

	openButton.setAttribute('aria-expanded', 'true');
	closeButton.setAttribute('aria-expanded', 'true');
	document.body.classList.add('main-navigation-menu-open');
	document.body.style.overflow = 'hidden';

	// Set focus to the first link in the open menu
	const menu = document.getElementById('site-navigation');
	const focusableElements = menu.querySelectorAll('a');
	const firstFocusableElement = focusableElements[0];
	firstFocusableElement.focus();

	// close the menu if anything outside the menu gains focus
	document.addEventListener(
		'focusin',
		function (event) {
			if (!menu.contains(event.target)) {
				closeMobileMenu();
			}
		},
		true
	);
}

function animateOpen(element) {
	element.style.height = 'auto'; // Set height to auto
	const height = element.offsetHeight; // Get actual height
	element.style.height = '0px'; // Reset to 0

	// Animate using requestAnimationFrame for smooth performance
	requestAnimationFrame(() => {
		let currentHeight = 0;
		const step = 10; // Adjust for animation speed
		element.style.visibility = 'visible';

		const interval = setInterval(() => {
			currentHeight += step;
			element.style.height = `${currentHeight}px`;

			if (currentHeight >= height) {
				clearInterval(interval);
			}
		}, 10); // Adjust for animation speed
	});
}

function animateClose(element) {
	const height = element.offsetHeight; // Get actual height

	// Animate using requestAnimationFrame for smooth performance
	requestAnimationFrame(() => {
		let currentHeight = height;
		const step = 10; // Adjust for animation speed

		const interval = setInterval(() => {
			currentHeight -= step;
			element.style.height = `${currentHeight}px`;

			if (currentHeight <= 0) {
				clearInterval(interval);
				element.style.height = '0px'; // Reset to 0
			}
		}, 10); // Adjust for animation speed
	});

	setTimeout(() => {
		element.style.visibility = 'hidden';
	}, 300); // Adjust for animation speed
}

function removeMobileStyles() {
	// remove the inline styles added by the animateOpen and animateClose functions
	const subMenus = document.querySelectorAll('.sub-menu');
	for (const subMenu of subMenus) {
		subMenu.style.height = '';
		subMenu.style.visibility = '';
	}

	// set all the sub-menu-toggle buttons to aria-expanded false
	const subMenuToggles = document.querySelectorAll('.sub-menu-toggle');
	for (const subMenuToggle of subMenuToggles) {
		subMenuToggle.setAttribute('aria-expanded', 'false');
	}

	// remove the .toggled class from all the menu items with submenus
	const menuItemsWithChildren = document.querySelectorAll(
		'.menu-item-has-children'
	);
	for (const menuItem of menuItemsWithChildren) {
		menuItem.classList.remove('toggled');
	}
}

export function mobileNavigation() {
	const siteNavigation = document.getElementById('site-navigation');
	const menu = siteNavigation.getElementsByTagName('ul')[0];
	const openButton = document.querySelector('.main-navigation-open');

	// Hide the menu toggle button if the menu is empty and return early.
	if ('undefined' === typeof menu) {
		openButton.style.display = 'none';
		return;
	}

	// Get the mobile breakpoint variable from CSS to use with the resize observer.
	const mobileBreakpoint = getComputedStyle(siteNavigation).getPropertyValue(
		'--pdm-menu-breakpoint'
	);

	if (!menu.classList.contains('nav-menu')) {
		menu.classList.add('nav-menu');
	}

	// Toggle the .toggled class and the aria-expanded value each time the buttons are clicked.
	openButton.addEventListener('click', function () {
		openMobileMenu();
	});

	const closeButton = document.querySelector('.main-navigation-close');
	closeButton.addEventListener('click', closeMobileMenu);

	// Remove the .toggled class and set aria-expanded to false when the user clicks outside the navigation.
	document.addEventListener('click', function (event) {
		const isClickInside = siteNavigation.contains(event.target);
		const isClickOpenButton = openButton.contains(event.target);

		if (!isClickInside && !isClickOpenButton) {
			closeMobileMenu();
		}
	});

	// Get the menu items with submenus
	const menuItemsWithChildren = menu.querySelectorAll(
		'.nav-menu > .menu-item-has-children'
	);

	// add a button to show the submenu after the link in the menu item
	for (const menuItem of menuItemsWithChildren) {
		const menuItemLink = menuItem.querySelector('a');
		const menuItemButton = document.createElement('button');
		menuItemButton.setAttribute('aria-expanded', 'false');
		menuItemButton.setAttribute('aria-label', 'Show submenu');
		menuItemButton.setAttribute('class', 'sub-menu-toggle');
		menuItemButton.innerHTML =
			'<span class="screen-reader-text">Show submenu</span>';
		menuItem.insertBefore(menuItemButton, menuItemLink.nextSibling);

		// reveal the submenu when the button is clicked
		menuItemButton.addEventListener('click', function () {
			const isExpanded =
				menuItemButton.getAttribute('aria-expanded') === 'true';
			menuItemButton.setAttribute('aria-expanded', !isExpanded);
			menuItem.classList.toggle('toggled');

			const subMenu = menuItem.querySelector('.sub-menu');
			if (isExpanded) {
				animateClose(subMenu);
			} else {
				animateOpen(subMenu);
			}
		});

		// if the menu item is an empty link, make it also expand the submenu
		if (menuItemLink.getAttribute('href') === '#') {
			menuItemLink.addEventListener('click', function (event) {
				event.preventDefault();
				menuItemButton.click();
			});
		}
	}

	// close the menu when escape key is pressed
	document.addEventListener('keydown', function (event) {
		if (event.key === 'Escape') {
			closeMobileMenu();
		}
	});

	// add a resize observer to remove the inline styles added by the animateOpen and animateClose functions
	const resizeObserver = new ResizeObserver((entries) => {
		for (const entry of entries) {
			if (entry.contentBoxSize) {
				if (entry.contentBoxSize[0].inlineSize > mobileBreakpoint) {
					removeMobileStyles();
				}
			}
		}
	});

	resizeObserver.observe(document.body);
}
